import React, { useState, useRef, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { FaMapMarkerAlt, FaSearch, FaTimes } from 'react-icons/fa';

// Fix for default markers in react-leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const MapSearchModal = ({ isVisible, onClose, onLocationSelect, initialLocation = null }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(initialLocation);
  const [mapCenter, setMapCenter] = useState(initialLocation || { lat: 17.3850, lng: 78.4867 });
  const [address, setAddress] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const searchRef = useRef(null);

  // Map click handler
  const MapClickHandler = () => {
    useMapEvents({
      click(e) {
        const lat = e.latlng.lat;
        const lng = e.latlng.lng;
        const newLocation = { lat, lng };
        setSelectedLocation(newLocation);
        setMapCenter(newLocation);
        fetchAddress(lat, lng);
      },
    });
    return null;
  };

  // Fetch address from coordinates
  const fetchAddress = async (lat, lng) => {
    if (!lat || !lng || isNaN(lat) || isNaN(lng)) {
      console.warn('Invalid coordinates provided to fetchAddress:', lat, lng);
      return;
    }

    try {
      setIsLoading(true);
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&accept-language=en`,
        {
          headers: {
            'Accept': 'application/json',
          }
        }
      );

      if (response.status === 429) {
        console.warn('Nominatim API rate limited (429)');
        setAddress('Rate limit exceeded. Please wait...');
        return;
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data && data.display_name) {
        setAddress(data.display_name);
      } else {
        setAddress('Address not found');
      }
    } catch (error) {
      console.error('Error fetching address:', error);
      setAddress('Error fetching address. Please check your connection.');
    } finally {
      setIsLoading(false);
    }
  };

  // Search for places
  const searchPlaces = async (query) => {
    if (!query || !query.trim()) {
      setSearchResults([]);
      setShowSearchResults(false);
      return;
    }

    try {
      setIsLoading(true);
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=10&accept-language=en&addressdetails=1`
      );
      const data = await response.json();
      setSearchResults(data);
      setShowSearchResults(true);
    } catch (error) {
      console.error('Error searching places:', error);
      alert('Error searching for place. Please check your internet connection.');
      setSearchResults([]);
      setShowSearchResults(false);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle search input change
  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);

    // Debounce search
    clearTimeout(searchRef.current);
    searchRef.current = setTimeout(() => {
      searchPlaces(query);
    }, 300);
  };

  // Handle search result selection
  const handleResultSelect = (result) => {
    const lat = parseFloat(result.lat);
    const lng = parseFloat(result.lon);
    const newLocation = { lat, lng };

    setSelectedLocation(newLocation);
    setMapCenter(newLocation);
    setAddress(result.display_name);
    setSearchQuery(result.display_name);
    setShowSearchResults(false);
  };

  // Handle confirm selection
  const handleConfirm = () => {
    if (selectedLocation) {
      onLocationSelect({
        location: selectedLocation,
        address: address
      });
      onClose();
    }
  };

  // Get current location
  const getCurrentLocation = () => {
    if (navigator.geolocation) {
      setIsLoading(true);
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const lat = position.coords.latitude;
          const lng = position.coords.longitude;
          const newLocation = { lat, lng };
          setSelectedLocation(newLocation);
          setMapCenter(newLocation);
          fetchAddress(lat, lng);
        },
        (error) => {
          console.error('Error getting current location:', error);
          alert('Unable to get current location');
        },
        { enableHighAccuracy: true }
      );
    } else {
      alert('Geolocation is not supported by this browser');
    }
  };

  // Initialize with current location if no initial location
  useEffect(() => {
    if (!initialLocation && isVisible) {
      getCurrentLocation();
    }
  }, [isVisible]);

  if (!isVisible) return null;

  return (
    <div className="map-modal-overlay">
      <div className="map-modal-content">
        <div className="map-modal-header">
          <h3>Select Location</h3>
          <button className="close-button" onClick={onClose}>
            <FaTimes />
          </button>
        </div>

        <div className="map-search-container">
          <div className="search-input-container">
            <FaSearch className="search-icon" />
            <input
              type="text"
              placeholder="Search for a location..."
              value={searchQuery}
              onChange={handleSearchChange}
              className="map-search-input"
            />
            {isLoading && <div className="loading-spinner"></div>}
          </div>

          {showSearchResults && searchResults.length > 0 && (
            <div className="search-results">
              {searchResults.map((result, index) => (
                <div
                  key={index}
                  className="search-result-item"
                  onClick={() => handleResultSelect(result)}
                >
                  <FaMapMarkerAlt className="result-icon" />
                  <div className="result-text">
                    <div className="result-name">{result.display_name}</div>
                    {result.address && (
                      <div className="result-address">
                        {result.address.city || result.address.town || result.address.village || ''}
                        {result.address.state && `, ${result.address.state}`}
                        {result.address.country && `, ${result.address.country}`}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="map-container">
          <MapContainer
            center={[mapCenter.lat, mapCenter.lng]}
            zoom={13}
            style={{ height: '400px', width: '100%' }}
          >
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            />
            <MapClickHandler />
            {selectedLocation && (
              <Marker position={[selectedLocation.lat, selectedLocation.lng]} />
            )}
          </MapContainer>
        </div>

        {selectedLocation && (
          <div className="selected-location-info">
            <div className="location-details">
              <FaMapMarkerAlt className="location-icon" />
              <div className="location-text">
                <div className="coordinates">
                  Lat: {selectedLocation.lat.toFixed(6)}, Lng: {selectedLocation.lng.toFixed(6)}
                </div>
                {address && <div className="address">{address}</div>}
              </div>
            </div>
          </div>
        )}

        <div className="map-modal-footer">
          <button className="current-location-btn" onClick={getCurrentLocation}>
            <FaMapMarkerAlt />
            Use Current Location
          </button>
          <div className="action-buttons">
            <button className="cancel-btn" onClick={onClose}>
              Cancel
            </button>
            <button
              className="confirm-btn"
              onClick={handleConfirm}
              disabled={!selectedLocation}
            >
              Confirm Location
            </button>
          </div>
        </div>
      </div>

      <style jsx>{`
        .map-modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: rgba(0, 0, 0, 0.5);
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 1000;
        }

        .map-modal-content {
          background: white;
          border-radius: 12px;
          width: 90%;
          max-width: 800px;
          max-height: 90vh;
          overflow: hidden;
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
        }

        .map-modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 20px;
          border-bottom: 1px solid #eee;
        }

        .map-modal-header h3 {
          margin: 0;
          color: #333;
        }

        .close-button {
          background: none;
          border: none;
          font-size: 20px;
          cursor: pointer;
          color: #666;
          padding: 5px;
        }

        .close-button:hover {
          color: #333;
        }

        .map-search-container {
          position: relative;
          padding: 20px;
          border-bottom: 1px solid #eee;
        }

        .search-input-container {
          position: relative;
          display: flex;
          align-items: center;
        }

        .search-icon {
          position: absolute;
          left: 12px;
          color: #666;
          z-index: 1;
        }

        .map-search-input {
          width: 100%;
          padding: 12px 12px 12px 40px;
          border: 2px solid #ddd;
          border-radius: 8px;
          font-size: 16px;
          outline: none;
          transition: border-color 0.3s;
        }

        .map-search-input:focus {
          border-color: #007bff;
        }

        .loading-spinner {
          position: absolute;
          right: 12px;
          width: 20px;
          height: 20px;
          border: 2px solid #f3f3f3;
          border-top: 2px solid #007bff;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        .search-results {
          position: absolute;
          top: 100%;
          left: 0;
          right: 0;
          background: white;
          border: 1px solid #ddd;
          border-top: none;
          border-radius: 0 0 8px 8px;
          max-height: 200px;
          overflow-y: auto;
          z-index: 9999;
        }

        .search-result-item {
          display: flex;
          align-items: center;
          padding: 12px;
          cursor: pointer;
          border-bottom: 1px solid #f0f0f0;
        }

        .search-result-item:hover {
          background-color: #f8f9fa;
        }

        .result-icon {
          color: #007bff;
          margin-right: 10px;
          flex-shrink: 0;
        }

        .result-text {
          flex: 1;
        }

        .result-name {
          font-weight: 500;
          color: #333;
          margin-bottom: 2px;
        }

        .result-address {
          font-size: 12px;
          color: #666;
        }

        .map-container {
          padding: 20px;
        }

        .selected-location-info {
          padding: 20px;
          background-color: #f8f9fa;
          border-top: 1px solid #eee;
        }

        .location-details {
          display: flex;
          align-items: flex-start;
        }

        .location-icon {
          color: #28a745;
          margin-right: 10px;
          margin-top: 2px;
          flex-shrink: 0;
        }

        .location-text {
          flex: 1;
        }

        .coordinates {
          font-family: monospace;
          font-size: 12px;
          color: #666;
          margin-bottom: 4px;
        }

        .address {
          color: #333;
          font-size: 14px;
        }

        .map-modal-footer {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 20px;
          border-top: 1px solid #eee;
        }

        .current-location-btn {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 10px 16px;
          background-color: #6c757d;
          color: white;
          border: none;
          border-radius: 6px;
          cursor: pointer;
          font-size: 14px;
        }

        .current-location-btn:hover {
          background-color: #5a6268;
        }

        .action-buttons {
          display: flex;
          gap: 10px;
        }

        .cancel-btn, .confirm-btn {
          padding: 10px 20px;
          border: none;
          border-radius: 6px;
          cursor: pointer;
          font-size: 14px;
          font-weight: 500;
        }

        .cancel-btn {
          background-color: #6c757d;
          color: white;
        }

        .cancel-btn:hover {
          background-color: #5a6268;
        }

        .confirm-btn {
          background-color: #007bff;
          color: white;
        }

        .confirm-btn:hover:not(:disabled) {
          background-color: #0056b3;
        }

        .confirm-btn:disabled {
          background-color: #ccc;
          cursor: not-allowed;
        }
      `}</style>
    </div>
  );
};

export default MapSearchModal;
